import { useMemo, useState } from "react";
import { PlayIcon, XIcon } from "lucide-react";
import { createPortal } from "react-dom";

import {
  imageShape,
  type WebFlowCollectionItem,
} from "@tudigo-monorepo/core-tudigo-api-models";
import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import { useGetWebflowInvestorTestimonials } from "@tudigo-monorepo/web-tudigo-api-client";
import { ButtonRebrand } from "@tudigo-monorepo/web-tudigo-components";

import {
  MediaSlider,
  MediaSliderContent,
  MediaSliderControls,
  MediaSliderItem,
  MediaSliderThumbItem,
  MediaSliderThumbMenu,
} from "../../../shared-internal/components/search-projects/media-slider/media-slider";
import type { WebflowTestimonial } from "../../models/webflow-testimonial";
import { formatWebflowItemTestimonialData } from "../../utils/webflow-testimonials.utils";

const TESTIMONIALS_LIMIT = 6;

function TestimonialVideoPlayer({
  testimonial,
}: {
  testimonial: WebflowTestimonial;
}) {
  const [isOpen, setIsOpen] = useState(false);

  if (!testimonial.previewVideo) {
    return null;
  }

  return (
    <>
      {isOpen &&
        !!testimonial.fullVideoEmbedHTML &&
        createPortal(
          <div className="z-dialog fixed bottom-0 left-0 right-0 top-0 flex items-center justify-center bg-black [&_iframe]:m-auto [&_iframe]:h-screen [&_iframe]:w-[100vw] [&_iframe]:md:w-[80vw]">
            <ButtonRebrand
              variant="ghost"
              className="group absolute right-6 top-6 mb-auto ml-auto p-4 hover:bg-transparent"
              onClick={() => setIsOpen(false)}
            >
              <XIcon className="h-6 w-6 text-white transition-opacity group-hover:opacity-70" />
            </ButtonRebrand>
            <div
              dangerouslySetInnerHTML={{
                __html: testimonial.fullVideoEmbedHTML,
              }}
            />
          </div>,
          document.body,
        )}

      <div
        className={cn(
          "relative flex h-full w-full items-center justify-center overflow-hidden rounded-[7px] bg-gray-200",
          "max-h-[240px] min-h-[240px] w-full lg:max-h-[320px] lg:min-h-[320px] lg:min-w-[320px] lg:max-w-[320px] 2xl:min-w-[464px] 2xl:max-w-[664px]",
        )}
      >
        <ButtonRebrand
          variant="ghost"
          onClick={() => setIsOpen(true)}
          className="absolute z-10 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-white opacity-70"
        >
          <PlayIcon className="fill-black-primary text-black-primary h-10 w-10 rounded-full" />
        </ButtonRebrand>
        <video
          className="aspect-video h-full w-full object-cover object-center"
          playsInline
          autoPlay
          muted
          loop
        >
          <source
            src={testimonial.previewVideo?.data.iframeUrl}
            type="video/mp4"
          />
        </video>
      </div>
    </>
  );
}

function TestimonialSlideContentDetails({
  testimonial,
}: {
  testimonial: WebflowTestimonial;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full w-full flex-col items-center gap-4 lg:items-start">
      {testimonial.quote && (
        <header className="flex w-full flex-col items-center gap-y-4 lg:items-start">
          <h1 className="font-roobert text-black-primary text-center text-[28px] font-semibold leading-[38px] lg:text-left lg:leading-8">
            {t(
              "search_projects_page.financed.investor_testimonials_carousel.title",
            )}
          </h1>
          <blockquote className="font-roobert text-black-900 line-clamp-4 text-pretty text-center text-lg lg:line-clamp-[7] lg:text-left lg:text-[20px]">
            {`"${testimonial.quote}"`}
          </blockquote>
        </header>
      )}
      <footer className="mt-auto flex w-full items-center justify-center lg:justify-between">
        <div className="flex h-full flex-col items-center lg:items-start">
          <h4 className="font-roobert text-black-primary mb-1 text-lg font-semibold leading-[25.2px]">
            {testimonial.investor?.shortName}
          </h4>
          <p className="font-roobert text-black-primary text-sm font-normal leading-[19.6px]">
            {testimonial.investor?.description}
          </p>
        </div>
        <MediaSliderControls
          className="hidden rounded-full bg-transparent lg:flex"
          variant="secondary"
        />
      </footer>
    </div>
  );
}

function TestimonialSlideContent({
  testimonial,
}: {
  testimonial: WebflowTestimonial;
}) {
  const canShowVideo = !!(
    testimonial.previewVideo && testimonial.fullVideoEmbedHTML
  );

  return (
    <article className="flex h-full w-full flex-col items-start gap-6 rounded-md lg:flex-row-reverse">
      {canShowVideo ? (
        <TestimonialVideoPlayer testimonial={testimonial} />
      ) : (
        <div className="block h-[240px] w-full lg:h-full lg:w-[364px] 2xl:w-[664px]" />
      )}
      <MediaSliderControls className="mx-auto lg:hidden" />
      <TestimonialSlideContentDetails testimonial={testimonial} />
    </article>
  );
}

export function InvestorTestimonialsSlider() {
  const { data } = useGetWebflowInvestorTestimonials();

  const testimonials = useMemo(() => {
    const availableItems =
      (data as { items?: WebFlowCollectionItem[] } | undefined)?.items
        ?.filter(
          (item) =>
            !item.isArchived &&
            !item.isDraft &&
            !!item.fieldData.affichageSurLaHome,
        )
        .slice(0, TESTIMONIALS_LIMIT) ?? [];

    return availableItems
      .map((item) => formatWebflowItemTestimonialData(item))
      .sort((a, b) => (a.displayPosition ?? 0) - (b.displayPosition ?? 0));
  }, [data]);

  const [currentSlide, setCurrentSlide] = useState(0);

  return (
    <MediaSlider
      className="min-h-[623px] gap-6 lg:min-h-[320px]"
      onSlideChange={setCurrentSlide}
    >
      <MediaSliderContent>
        {testimonials.map((testimonial, index) => (
          <MediaSliderItem
            key={testimonial.id}
            slide={index}
            className="h-full"
          >
            <TestimonialSlideContent testimonial={testimonial} />
          </MediaSliderItem>
        ))}
      </MediaSliderContent>
      <MediaSliderThumbMenu className="bg-transparent p-0">
        {testimonials
          .filter((_, index) => index !== currentSlide)
          .map((testimonial, index) => {
            const thumbUrl = testimonial.investor?.thumbnailUrl ?? "";

            return (
              <MediaSliderThumbItem
                key={testimonial.id}
                slide={index}
                media={{
                  type: "image",
                  data: { ...imageShape, url: thumbUrl },
                }}
              />
            );
          })}
      </MediaSliderThumbMenu>
    </MediaSlider>
  );
}
